<template>
    <div class="dashboard__content__box rlms-w-100">
        <div class="overview__box__wrapper" style="padding-inline: 0;">
            <div class="section__title__box">
                <h4 class="title">Dashboard</h4>
                <div>
                    <VueDatePicker v-model="date" range :enable-time-picker="false" auto-apply
                        @update:model-value="fetchData" :clearable="false" style="width: 260px" />
                </div>
            </div>

            <!-- Dashboard cards -->
            <div class="data__box__wrap">
                <div class="data__box color1">
                    <div class="data__icon"><span class="material-symbols-outlined"
                            style="font-size: 58px;color: #6bd35e;"> paid </span></div>
                    <div class="data__details"><span class="data__title">Total Earning</span><span
                            class="data__output">{{ formatCurrency(totalEarning) }}</span></div>
                </div>
                <div class="data__box color1">
                    <div class="data__icon"><span class="material-symbols-outlined"
                            style="font-size: 58px;color: #725cff;"> domain </span></div>
                    <div class="data__details"><span class="data__title">Total Organizations</span><span
                            class="data__output">{{ organizations }}</span></div>
                </div>
                <div class="data__box color3">
                    <div class="data__icon"><span class="material-symbols-outlined"
                            style="font-size: 58px; color: #ffb42c;"> groups </span></div>
                    <div class="data__details"><span class="data__title">Users</span><span
                            class="data__output">{{ users }}</span></div>
                </div>
                <div class="data__box color2">
                    <div class="data__icon"><span class="material-symbols-outlined"
                            style="font-size: 58px;color: #35acfc;"> groups </span></div>
                    <div class="data__details"><span class="data__title">Total Learners</span><span
                            class="data__output">{{ learners }}</span></div>
                </div>
            </div>

            <div class="chart-container" style="margin-top: 40px; width: 100%;">
                <h4>Sales Details</h4>
                <ApexCharts ref="salesChart" type="bar" height="400" :options="salesChartOptions"
                    :series="salesSeries" />
            </div>

            <div class="rlms-flex rlms-gap-15" style="margin-top: 40px;width: 100%; ">
                <div class="chart-container" style="width: 50%;">
                    <div class="section__title__box">
                        <h4 class="title">Users Created</h4>
                    </div>
                    <!-- Users chart -->
                    <div>
                        <ApexCharts ref="usersChart" type="line" height="400" :options="userChartOptions"
                            :series="userSeries" />
                    </div>
                </div>

                <!-- Learners chart -->
                <div class="chart-container" style="width: 50%;">
                    <div class="section__title__box">
                        <h4 class="title">Learners Created</h4>
                    </div>
                    <div>
                        <ApexCharts ref="learnersChart" type="line" height="400" :options="learnerChartOptions"
                            :series="learnerSeries" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import ApexCharts from 'vue3-apexcharts'
import api from '@/services/axios'
import { useLoaderStore } from '@/stores/loader'
import { handleError } from '@/utils/handleError'
import { formatCurrency } from '@/utils/format-helpers'

const totalEarning = ref(0)
const organizations = ref(0)
const users = ref(0)
const learners = ref(0)
const actuveLearners = ref(0)
const loader = useLoaderStore()

const date = ref([])

// ApexCharts state
const salesChart = ref(null)
const usersChart = ref(null)
const learnersChart = ref(null)

const salesChartOptions = ref({
    chart: {
        id: 'sales-chart',
        type: 'bar',
        toolbar: { show: true }
    },
    xaxis: {
        categories: [] // will be updated from API
    },
    yaxis: {
        title: { text: 'Amount' }
    },
    colors: ['#6bd35e'], // green bars
    plotOptions: {
        bar: {
            columnWidth: '50%',
            borderRadius: 4
        }
    },
    tooltip: {
        y: {
            formatter: value => `$${value}`
        }
    }
})

const userChartOptions = ref({
    chart: { id: 'users-chart', zoom: { enabled: true } },
    xaxis: { categories: [] },
    yaxis: { title: { text: 'Count' } },
    stroke: { curve: 'smooth' },
    colors: ['#725cff']
})

const learnerChartOptions = ref({
    chart: { id: 'learners-chart', zoom: { enabled: true } },
    xaxis: { categories: [] },
    yaxis: { title: { text: 'Count' } },
    stroke: { curve: 'smooth' },
    colors: ['#35acfc']
})

const salesSeries = ref([
    {
        name: 'Sales',
        data: [] // dynamic from API
    }
])
const userSeries = ref([{ name: 'Users Created', data: [] }])
const learnerSeries = ref([{ name: 'Learners Created', data: [] }])

// Fetch data
const fetchData = async () => {
    try {
        loader.show()
        const [startDate, endDate] = date.value

        const { data } = await api.get('admin/dashboard', {
            params: { from: startDate, to: endDate }
        })

        // Dashboard cards
        totalEarning.value = data?.data?.total_earning || 0
        organizations.value = data?.data?.organizations_count || 0
        users.value = data?.data?.users_count || 0
        learners.value = data?.data?.learners_count || 0
        actuveLearners.value = data?.data?.active_learners_count || 0

        // Chart data
        if (data?.data?.chart) {
            // Update Sales chart
            salesChart.value.updateOptions({
                xaxis: { categories: data.data.chart.labels }
            })

            salesSeries.value = [ { name: 'Sales', data: data.data.chart.sales } ];

            // Users chart
            usersChart.value.updateOptions({
                xaxis: { categories: data.data.chart.labels }
            })
            userSeries.value = [{ name: 'Users Created', data: data.data.chart.users }]

            // Learners chart
            learnersChart.value.updateOptions({
                xaxis: { categories: data.data.chart.labels }
            })
            learnerSeries.value = [{ name: 'Learners Created', data: data.data.chart.learners }]
        }
    } catch (error) {
        handleError(error)
    } finally {
        loader.hide()
    }
}

// Initialize date range: last month → today
onMounted(() => {
    const today = new Date()
    const lastMonth = new Date()
    lastMonth.setDate(today.getDate() - 30)
    date.value = [lastMonth, today]

    fetchData()
})
</script>
<style lang="css" scoped>
.chart-container {
    padding: 20px;
    border: 1px solid #eee;
    border-radius: 16px;
}
</style>